import { ProFormSelect } from "@ant-design/pro-components";
import { useIntl } from "@umijs/max";
import type { SelectProps } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import React, { useEffect, useMemo } from "react";
import { useGameTagStore } from "@/stores/useGameTagStore";

type ProFormSelectComponentProps = React.ComponentProps<typeof ProFormSelect>;

interface GameTagSelectProps
  extends Omit<
    ProFormSelectComponentProps,
    "name" | "label" | "fieldProps" | "request" | "options"
  > {
  /** 是否支持多选 */
  multiple?: boolean;
  /** 表单字段名 */
  name?: ProFormSelectComponentProps["name"];
  /** 标签文本 */
  label?: React.ReactNode;
  /** 占位提示语 */
  placeholder?: string;
  /** fieldProps 透传 */
  fieldProps?: SelectProps<number | number[], DefaultOptionType>;
}

/**
 * 公共游戏标签选择组件
 * 数据接口: /api/v1/sys/listGameTag
 * 数据缓存: src/stores/useGameTagStore.ts
 */
const GameTagSelect: React.FC<GameTagSelectProps> = ({
  multiple = false,
  name = "gameTagIds",
  label,
  placeholder,
  fieldProps,
  ...rest
}) => {
  const intl = useIntl();
  const gameTags = useGameTagStore((state) => state.gameTags);
  const loading = useGameTagStore((state) => state.loading);
  const fetchGameTags = useGameTagStore((state) => state.fetchGameTags);

  useEffect(() => {
    void fetchGameTags();
  }, [fetchGameTags]);

  const options = useMemo(
    () =>
      gameTags.map((gameTag) => ({
        label: gameTag.name,
        value: gameTag.id,
      })),
    [gameTags],
  );

  const selectMode: "multiple" | undefined = multiple ? "multiple" : undefined;

  const mergedFieldProps = useMemo(
    () => ({
      mode: selectMode,
      showSearch: true,
      optionFilterProp: "label",
      placeholder:
        placeholder ||
        intl.formatMessage({
          id: "pages.games.tag.placeholder",
          defaultMessage: "请选择游戏标签",
        }),
      allowClear: true,
      loading,
      ...fieldProps,
    }),
    [selectMode, placeholder, intl, loading, fieldProps],
  );

  return (
    <ProFormSelect
      name={name}
      label={
        label ||
        intl.formatMessage({
          id: "pages.games.tag.label",
          defaultMessage: "游戏标签",
        })
      }
      options={options}
      fieldProps={mergedFieldProps}
      {...rest}
    />
  );
};

export default GameTagSelect;
